import { Component, EventEmitter, inject, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { IonButton, IonButtons, IonIcon, IonText, IonToolbar, MenuController } from '@ionic/angular';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';

import { COMPANY_MENU_ID, USER_MENU_ID } from '@ninety/mobile/platform/api';
import { UserNamePipe } from '@ninety/mobile/platform/services';
import { CompanyUserSelectors, CurrentUserSelectors } from '@ninety/mobile/platform/state';

import { CompanyAvatarComponent } from '../company-avatar/company-avatar.component';
import { UserAvatarComponent } from '../user-avatar/user-avatar.component';

type SideMenuToolbarMode = 'company' | 'user';

@Component({
  selector: 'nty-side-menu-toolbar',
  imports: [
    LetDirective,
    UserAvatarComponent,
    CompanyAvatarComponent,
    UserNamePipe,
    IonButton,
    IonButtons,
    IonIcon,
    IonText,
    IonToolbar,
  ],
  template: `
    <ng-container *ngrxLet="vm$ as vm">
      <ion-toolbar>
        <div slot="start" class="img-with-name-container">
          @if (mode === 'company') {
            <nty-company-avatar size="36px" />
          } @else {
            <nty-user-avatar
              slot="end"
              id="user-avatar-menu"
              [userInitials]="vm.user?.initials || ''"
              [avatarUrl]="vm.user?.avatarUrl || ''" />
          }

          <ion-text class="terra-headline-3 custom-name-text">
            {{ mode === 'company' ? vm.currentCompanyUser?.company?.name : (vm.user | userName) }}
          </ion-text>
        </div>

        <ion-buttons slot="end">
          <ion-button size="small" (click)="onClose()" aria-label="Close">
            <ion-icon src="assets/icons/x--regular.svg" slot="icon-only" />
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      @use 'terra';

      ion-toolbar {
        --padding-top: 40px;
        --padding-end: 24px;
        --padding-bottom: 24px;
        --padding-start: 24px;

        --background: #{terra.color(background, heavy)};
        --border-color: transparent;
      }

      .img-with-name-container {
        display: flex;
        align-items: center;
        gap: 8px;
        max-width: 85%;

        & > :first-child {
          flex-shrink: 0;
        }
      }

      .custom-name-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: terra.color(text);
      }

      ion-avatar {
        width: 36px;
        height: 36px;
      }

      ion-button {
        --color: #{terra.color(neutral-mid, 400)};
      }
    `,
  ],
})
export class SideMenuToolbarComponent {
  @Input() mode: SideMenuToolbarMode = 'company';

  @Output() closed = new EventEmitter<void>();

  private store = inject(Store);
  private menuCtrl = inject(MenuController);
  private companyMenuId = inject(COMPANY_MENU_ID);
  private userMenuId = inject(USER_MENU_ID);

  public readonly vm$ = {
    currentCompanyUser: this.store.select(CompanyUserSelectors.selectCurrentCompanyUser),
    user: this.store.select(CurrentUserSelectors.selectUser),
  };

  onClose() {
    const menuMap: Record<SideMenuToolbarMode, string> = {
      company: this.companyMenuId,
      user: this.userMenuId,
    };
    this.menuCtrl.close(menuMap[this.mode]);
    this.closed.emit();
  }
}
